import React, { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { AppShell } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Lock } from "lucide-react";
import { fmtDate } from "@/lib/format";
import {
  CreditCard, LogOut, Loader2, Sparkles, Bell, HardHat, LineChart,
  Building2, KeyRound, Trash2, LifeBuoy, FileText, Pencil, Upload, ChevronRight,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { InstallButton } from "@/components/InstallButton";
import { ThemeToggle } from "@/components/ThemeToggle";
import { getPushState, enablePush, disablePush, sendTestPush } from "@/lib/push";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { TradePicker } from "@/components/TradePicker";
import { VendorsCard } from "@/components/VendorsCard";

const SectionLabel = ({ children }) => (
  <div className="mb-2 mt-7 px-1 text-[11px] font-bold uppercase tracking-[0.14em] text-muted-foreground">{children}</div>
);

// ---------------- Profile + company branding ----------------
function ProfileCard() {
  const { user, refresh } = useAuth();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(user?.full_name || "");
  const [company, setCompany] = useState(user?.company_name || "");
  const [address, setAddress] = useState(user?.company_address || "");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const isSandbox = user?.subscription?.status === "sandbox";
  useEffect(() => { setName(user?.full_name || ""); setCompany(user?.company_name || ""); setAddress(user?.company_address || ""); }, [user?.full_name, user?.company_name, user?.company_address]);

  const save = async () => {
    setSaving(true);
    try { await api.patch("/auth/profile", { full_name: name.trim(), company_name: company.trim(), company_address: address.trim() }); await refresh(); setOpen(false); toast.success("Profile updated"); }
    catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };
  const pickLogo = async (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      await api.post("/auth/company-logo", fd); await refresh(); toast.success("Logo updated");
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  };

  return (
    <div className="rounded-2xl border border-border bg-card p-5" data-testid="profile-card">
      <div className="flex items-center gap-3">
        {user?.company_logo_url ? (
          <img src={user.company_logo_url} alt="Company logo" className="h-12 w-12 rounded-xl object-contain ring-1 ring-border" data-testid="company-logo-img" />
        ) : (
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-500/15 font-heading text-lg font-bold text-emerald-500">
            {(user?.full_name || user?.email || "U").charAt(0).toUpperCase()}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="truncate font-semibold" data-testid="account-name">{user?.full_name || "—"}</div>
          <div className="truncate text-sm text-muted-foreground" data-testid="account-email">{user?.email}</div>
          {user?.company_name && <div className="mt-0.5 flex items-center gap-1 truncate text-xs text-emerald-500"><Building2 className="h-3 w-3" />{user.company_name}</div>}
        </div>
        {isSandbox ? (
          <Button size="sm" variant="outline" onClick={() => window.dispatchEvent(new Event("sp:convert"))} data-testid="edit-profile-locked" className="h-8 gap-1"><Lock className="h-3.5 w-3.5" /> Unlock</Button>
        ) : (
          <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid="edit-profile-btn" className="h-8">Edit</Button>
        )}
      </div>
      {isSandbox && (
        <p className="mt-3 flex items-center gap-1.5 rounded-lg border border-border bg-background px-3 py-2 text-xs text-muted-foreground" data-testid="branding-locked-note">
          <Lock className="h-3.5 w-3.5 shrink-0 text-emerald-500" /> Add your logo &amp; company name to reports and PDFs — free when you start your 15-day trial.
        </p>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="profile-dialog">
          <DialogHeader><DialogTitle>Edit profile & branding</DialogTitle>
            <DialogDescription>Your company name and logo appear on the reports and PDFs you send.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div><Label className="text-xs">Your name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="profile-name-input" placeholder="Jane Contractor" /></div>
            <div><Label className="text-xs">Company name</Label>
              <Input value={company} onChange={(e) => setCompany(e.target.value)} data-testid="profile-company-input" placeholder="Acme Builders LLC" /></div>
            <div><Label className="text-xs">Business address</Label>
              <Input value={address} onChange={(e) => setAddress(e.target.value)} data-testid="profile-address-input" placeholder="123 Main St, Austin, TX 78701" /></div>
            <div>
              <Label className="text-xs">Company logo</Label>
              <div className="mt-1 flex items-center gap-3">
                {user?.company_logo_url && <img src={user.company_logo_url} alt="" className="h-12 w-12 rounded-lg object-contain ring-1 ring-border" />}
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={pickLogo} data-testid="logo-file-input" />
                <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="logo-upload-btn" className="h-10 gap-2">
                  {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}{user?.company_logo_url ? "Replace" : "Upload"} logo
                </Button>
              </div>
              <p className="mt-1 text-xs text-muted-foreground">PNG or JPG, up to 3MB. Shown on shared reports & PDFs.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="profile-save-btn" className="bg-emerald-500 hover:bg-emerald-600">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------------- Change password ----------------
function SecurityCard() {
  const { user } = useAuth();
  const isSandbox = user?.subscription?.status === "sandbox";
  const [open, setOpen] = useState(false);
  const [cur, setCur] = useState("");
  const [nxt, setNxt] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (nxt.length < 8) { toast.error("New password must be at least 8 characters"); return; }
    setBusy(true);
    try { await api.post("/auth/password", { current_password: cur, new_password: nxt }); setOpen(false); setCur(""); setNxt(""); toast.success("Password changed"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't change password"); }
    finally { setBusy(false); }
  };
  if (isSandbox) {
    return (
      <button onClick={() => window.dispatchEvent(new Event("sp:convert"))} data-testid="change-password-locked" className="flex w-full items-center gap-3 rounded-2xl border border-border bg-card p-5 text-left transition-colors hover:bg-muted">
        <KeyRound className="h-5 w-5 text-emerald-500" />
        <div className="flex-1"><div className="text-sm font-semibold">Set a password</div><div className="text-xs text-muted-foreground">Create a free account to set your login</div></div>
        <Lock className="h-4 w-4 text-muted-foreground" />
      </button>
    );
  }
  return (
    <button onClick={() => setOpen(true)} data-testid="change-password-row" className="flex w-full items-center gap-3 rounded-2xl border border-border bg-card p-5 text-left transition-colors hover:bg-muted">
      <KeyRound className="h-5 w-5 text-emerald-500" />
      <div className="flex-1"><div className="text-sm font-semibold">Change password</div><div className="text-xs text-muted-foreground">Update your login password</div></div>
      <ChevronRight className="h-4 w-4 text-muted-foreground" />
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="password-dialog" onClick={(e) => e.stopPropagation()}>
          <DialogHeader><DialogTitle>Change password</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label className="text-xs">Current password</Label><Input type="password" value={cur} onChange={(e) => setCur(e.target.value)} data-testid="cur-password-input" /></div>
            <div><Label className="text-xs">New password</Label><Input type="password" value={nxt} onChange={(e) => setNxt(e.target.value)} data-testid="new-password-input" placeholder="At least 8 characters" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} disabled={busy || !cur || !nxt} data-testid="password-save-btn" className="bg-emerald-500 hover:bg-emerald-600">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </button>
  );
}

// ---------------- Delete account ----------------
function DangerCard() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/auth/delete-account", { password: pw || undefined, confirm });
      toast.success("Account deleted");
      try { await logout(); } catch { /* ignore */ }
      navigate("/");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't delete account"); setBusy(false); }
  };
  return (
    <button onClick={() => setOpen(true)} data-testid="delete-account-row" className="flex w-full items-center gap-3 rounded-2xl border border-rose-500/30 bg-card p-5 text-left transition-colors hover:bg-rose-500/5">
      <Trash2 className="h-5 w-5 text-rose-500" />
      <div className="flex-1"><div className="text-sm font-semibold text-rose-500">Delete account</div><div className="text-xs text-muted-foreground">Permanently erase your account and all data</div></div>
      <ChevronRight className="h-4 w-4 text-muted-foreground" />
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="delete-dialog" onClick={(e) => e.stopPropagation()}>
          <DialogHeader><DialogTitle className="text-rose-500">Delete account permanently</DialogTitle>
            <DialogDescription>This erases every job, report, photo and share link you own. This cannot be undone.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div><Label className="text-xs">Password (if you set one)</Label><Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="delete-password-input" placeholder="Leave blank for Google sign-in" /></div>
            <div><Label className="text-xs">Type DELETE to confirm</Label><Input value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="delete-confirm-input" placeholder="DELETE" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} disabled={busy || confirm !== "DELETE"} data-testid="delete-confirm-btn" className="bg-rose-600 hover:bg-rose-700">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Delete forever"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </button>
  );
}

// ---------------- Help & legal ----------------
function HelpCard() {
  const Row = ({ href, icon: Icon, label, external }) => (
    <a href={href} target={external ? "_blank" : undefined} rel={external ? "noopener noreferrer" : undefined} data-testid={`help-${label.toLowerCase().replace(/\s/g, "-")}`}
       className="flex items-center gap-3 px-5 py-4 transition-colors hover:bg-muted">
      <Icon className="h-5 w-5 text-emerald-500" /><span className="flex-1 text-sm font-medium">{label}</span><ChevronRight className="h-4 w-4 text-muted-foreground" />
    </a>
  );
  return (
    <div className="divide-y divide-border overflow-hidden rounded-2xl border border-border bg-card" data-testid="help-card">
      <Row href="mailto:support@send-proof.com" icon={LifeBuoy} label="Contact support" />
      <Row href="/privacy" icon={FileText} label="Privacy Policy" external />
      <Row href="/terms" icon={FileText} label="Terms of Service" external />
    </div>
  );
}

// ---------------- Notifications ----------------
function NotificationsCard() {
  const [state, setState] = useState("checking");
  const [busy, setBusy] = useState(false);
  useEffect(() => { getPushState().then(setState); }, []);
  const enable = async () => {
    setBusy(true);
    try {
      await enablePush(); setState("on"); toast.success("Phone notifications on");
      try { await sendTestPush(); } catch { /* ignore */ }
    } catch (e) {
      if (e?.message === "ios-install") { setState("ios-install"); toast.error("On iPhone, add Send-Proof to your Home Screen first, then enable notifications from the installed app"); return; }
      const denied = typeof Notification !== "undefined" && Notification.permission === "denied";
      setState(denied ? "denied" : "off");
      toast.error(e?.message === "denied" ? "Allow notifications in your browser, then try again" : "Couldn't enable notifications");
    } finally { setBusy(false); }
  };
  const disable = async () => { setBusy(true); try { await disablePush(); setState("off"); toast("Notifications off"); } finally { setBusy(false); } };
  if (state === "unsupported") return null;
  return (
    <div className="rounded-2xl border border-border bg-card p-5" data-testid="notifications-card">
      <div className="flex items-center gap-2 text-sm font-semibold text-foreground"><Bell className="h-4 w-4 text-emerald-500" /> Phone notifications</div>
      <p className="mt-1 text-sm text-muted-foreground">Get a live alert as the worker completes items and sends proof back.</p>
      {state === "denied" ? (
        <p className="mt-3 text-xs text-rose-500" data-testid="push-denied">Blocked in your browser. Enable notifications for this site in settings, then reload.</p>
      ) : state === "ios-install" ? (
        <div className="mt-3 rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-3 text-xs text-emerald-300" data-testid="push-ios-install">
          <span className="font-semibold">iPhone/iPad setup:</span> Apple only allows notifications from an installed app. Tap the Share icon in Safari → <span className="font-semibold">Add to Home Screen</span>, open Send-Proof from that new icon, then come back here and enable notifications.
        </div>
      ) : state === "on" ? (
        <div className="mt-3 flex gap-2">
          <Button variant="outline" data-testid="push-test-btn" onClick={() => sendTestPush()} className="h-10 flex-1">Send test</Button>
          <Button variant="outline" data-testid="push-disable-btn" onClick={disable} disabled={busy} className="h-10 flex-1">Turn off</Button>
        </div>
      ) : (
        <Button data-testid="push-enable-btn" onClick={enable} disabled={busy || state === "checking"} className="mt-3 h-11 w-full gap-2 bg-emerald-500 hover:bg-emerald-600">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Bell className="h-4 w-4" /> Enable notifications</>}
        </Button>
      )}
    </div>
  );
}

// ---------------- Trades ----------------
function TradesCard() {
  const { user, refresh } = useAuth();
  const isSandbox = user?.subscription?.status === "sandbox";
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState(user?.trades || []);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setPicked(user?.trades || []); }, [user?.trades]);
  const trades = user?.trades || [];
  const save = async () => {
    setSaving(true);
    try { await api.patch("/auth/profile", { trades: picked }); await refresh(); setOpen(false); toast.success("Trades updated"); }
    catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };
  return (
    <div className="rounded-2xl border border-border bg-card p-5" data-testid="trades-card">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-semibold"><HardHat className="h-4 w-4 text-emerald-500" /> My trades</div>
        {isSandbox ? (
          <Button size="sm" variant="outline" onClick={() => window.dispatchEvent(new Event("sp:convert"))} data-testid="trades-edit-locked" className="h-8 gap-1"><Lock className="h-3.5 w-3.5" /> Unlock</Button>
        ) : (
          <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid="trades-edit-btn" className="h-8">{trades.length ? "Edit" : "Add"}</Button>
        )}
      </div>
      {isSandbox && (
        <p className="mt-3 flex items-center gap-1.5 rounded-lg border border-border bg-background px-3 py-2 text-xs text-muted-foreground" data-testid="trades-locked-note">
          <Lock className="h-3.5 w-3.5 shrink-0 text-emerald-500" /> Save your trades to speed up vendor matching — free when you start your 15-day trial.
        </p>
      )}
      {trades.length ? (
        <div className="mt-3 flex flex-wrap gap-2">{trades.map((t) => <span key={t} className="rounded-full bg-emerald-500/15 px-3 py-1 text-xs font-semibold text-emerald-400">{t}</span>)}</div>
      ) : (
        <p className="mt-1 text-sm text-muted-foreground">Add your field(s) of work to speed up sorting and vendor matching.</p>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="trades-dialog">
          <DialogHeader><DialogTitle>What do you do?</DialogTitle></DialogHeader>
          <div className="max-h-[50vh] overflow-y-auto"><TradePicker value={picked} onChange={setPicked} /></div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="trades-save" className="bg-emerald-500 hover:bg-emerald-600">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function Account() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const sub = user?.subscription || {};
  const isMaster = user?.is_master;

  const manageBilling = async () => {
    setBusy(true);
    try {
      // Trial / no-subscription users have no billing to manage yet — send them to
      // checkout so they can actually subscribe. Only real subscribers get the portal.
      const hasSubscription = sub.status === "active" || sub.status === "past_due" || sub.status === "canceled";
      const endpoint = hasSubscription ? "/billing/portal" : "/billing/checkout";
      const { data } = await api.post(endpoint, { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) { toast.error(e?.response?.data?.detail || "Billing unavailable — please try again"); setBusy(false); }
  };

  const statusLabel = {
    trialing: "Free trial", active: "Active", past_due: "Payment failed",
    canceled: "Canceled", none: "No subscription",
  }[sub.status] || sub.status;

  return (
    <AppShell title="Settings" onBack={() => navigate("/dashboard")}>
      <div className="px-4 pt-5 sm:px-8">
        <h1 className="font-heading text-2xl font-bold" data-testid="account-title">Settings</h1>

        <SectionLabel>Account</SectionLabel>
        <ProfileCard />

        {user?.is_admin && (
          <button onClick={() => navigate("/testers")} data-testid="developer-analytics-btn"
            className="mt-4 flex w-full items-center gap-3 rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-5 text-left transition-colors hover:bg-emerald-500/15">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-500 text-white"><LineChart className="h-5 w-5" /></div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold text-emerald-400">Analytics</div>
              <div className="text-xs text-emerald-500/80">Overview, marketing & free accounts — private to you</div>
            </div>
          </button>
        )}

        <SectionLabel>Subscription & billing</SectionLabel>
        {isMaster ? (
          <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-5" data-testid="developer-card">
            <div className="flex items-center gap-2 text-sm font-semibold text-emerald-500"><Sparkles className="h-4 w-4" /> Developer account</div>
            <div className="mt-2 font-heading text-lg font-bold text-emerald-400">Unlimited access</div>
            <p className="mt-1 text-sm text-emerald-500">Your account bypasses the paywall. No subscription or billing required.</p>
          </div>
        ) : (
          <div className="rounded-2xl border border-border bg-card p-5">
            <div className="flex items-center gap-2 text-sm font-semibold text-foreground"><Sparkles className="h-4 w-4 text-emerald-500" /> Subscription</div>
            {sub.status === "trialing" && sub.trial_days_remaining != null && (
              <div className="mt-3 rounded-xl bg-emerald-500/10 px-4 py-3" data-testid="trial-banner">
                <div className="font-heading text-lg font-bold text-emerald-500">{sub.trial_days_remaining} days left</div>
                <div className="text-xs text-emerald-600">in your free trial</div>
              </div>
            )}
            <dl className="mt-4 space-y-2 text-sm">
              <div className="flex justify-between"><dt className="text-muted-foreground">Plan</dt><dd className="font-semibold">Send-Proof — $9.99/mo</dd></div>
              <div className="flex justify-between"><dt className="text-muted-foreground">Status</dt><dd className="font-semibold" data-testid="sub-status">{statusLabel}</dd></div>
              <div className="flex justify-between">
                <dt className="text-muted-foreground">{sub.status === "trialing" ? "First charge" : "Next charge"}</dt>
                <dd className="font-semibold" data-testid="next-charge">{fmtDate(sub.current_period_end || sub.trial_end)}</dd>
              </div>
            </dl>
            <Button data-testid="manage-billing-btn" onClick={manageBilling} disabled={busy} className="mt-4 h-11 w-full gap-2 bg-foreground text-background hover:bg-foreground/90">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CreditCard className="h-4 w-4" /> {(sub.status === "active" || sub.status === "past_due" || sub.status === "canceled") ? "Manage Billing" : "Subscribe now"}</>}
            </Button>
          </div>
        )}

        <SectionLabel>Notifications</SectionLabel>
        <NotificationsCard />

        <SectionLabel>Work profile</SectionLabel>
        <div className="space-y-4"><TradesCard /><VendorsCard /></div>

        <SectionLabel>App</SectionLabel>
        <div className="space-y-4">
          <InstallButton className="h-11 w-full gap-1 bg-foreground text-background hover:bg-foreground/90" />
          <div className="flex items-center justify-between rounded-2xl border border-border bg-card px-5 py-4" data-testid="appearance-card">
            <div><div className="text-sm font-semibold text-foreground">Appearance</div><div className="text-xs text-muted-foreground">Switch between dark and light</div></div>
            <ThemeToggle />
          </div>
        </div>

        <SectionLabel>Security</SectionLabel>
        <div className="space-y-4"><SecurityCard /><DangerCard /></div>

        <SectionLabel>Help & legal</SectionLabel>
        <HelpCard />

        <button data-testid="account-logout-btn" onClick={logout} className="mt-6 flex w-full items-center justify-center gap-2 rounded-2xl border border-border bg-card py-3.5 text-sm font-semibold text-rose-600">
          <LogOut className="h-4 w-4" /> Log out
        </button>

        <div className="mt-6 flex items-center justify-center gap-1.5 pb-6 text-xs text-muted-foreground">
          <img src="/logo-mark.webp" alt="" className="h-4 w-4 rounded" /> Send-Proof · send-proof.com
        </div>
      </div>
    </AppShell>
  );
}
